import { t } from "@/lib/i18n/interpolate";
import {
  asSentence,
  listConjunction,
  stripFinalPeriod,
} from "@/lib/tool-kit/publishing/authors";

/**
 * Funding statements, in two published patterns.
 *
 * - **Elsevier.** The "standard way" Elsevier's Guide for Authors asks for, which
 *   funders' compliance checks read: funders separated by semicolons, each followed by
 *   its location if given and its numbers in square brackets — "This work was supported
 *   by the National Institutes of Health [grant numbers xxxx, yyyy]; the Bill & Melinda
 *   Gates Foundation, Seattle, WA [grant number zzzz]; and …". With no funding, the
 *   guide prescribes one exact sentence.
 * - **Narrative (PLOS).** PLOS's financial disclosure asks for each grant number, the
 *   initials of the authors who received each award and the funder's website, then a
 *   sentence on the funders' role — and has its own sentence for no funding.
 */

export type FundingStyle = "elsevier" | "narrative";
export type FunderRole = "none" | "described" | "omit";

export interface FunderInput {
  name: string;
  /** "Seattle, WA" — Elsevier's pattern places it after the name. */
  location: string;
  /** Comma-, semicolon- or line-separated. */
  grants: string;
  /** Initials or names of the authors who received the award. */
  recipients: string;
  url: string;
}

export interface FundingTemplates {
  lead: string;
  /** "{name}, {location}" */
  withLocation: string;
  elsevierBracket: string;
  /** Between funders in Elsevier's pattern: "; " */
  elsevierSeparator: string;
  /** Before the last funder: "; and " */
  elsevierFinalSeparator: string;
  narrativeAward: string;
  narrativeAwardTo: string;
  narrativeTo: string;
  narrativeDetails: string;
  detailSeparator: string;
}

export interface FundingOptions {
  style: FundingStyle;
  locale: string;
  /** "grant number" / "grant numbers" in the page's language, for a count. */
  grantLabel: (count: number) => string;
}

// ASCII, Arabic (،؛) and full-width/ideographic (，；、) commas and semicolons all separate.
const RE_GRANT_SEPARATOR = /[,;\n،؛，；、]+/;
const RE_SPACES = /\s+/g;

/** Grant numbers as a clean list: trimmed, de-duplicated, in the order typed. */
export function parseGrantNumbers(raw: string): string[] {
  const seen: string[] = [];
  for (const piece of raw.split(RE_GRANT_SEPARATOR)) {
    const grant = piece.trim().replace(RE_SPACES, " ");
    if (grant !== "" && !seen.includes(grant)) {
      seen.push(grant);
    }
  }
  return seen;
}

/** Funders that have a name, in the order entered. */
export function namedFunders(funders: readonly FunderInput[]): FunderInput[] {
  return funders.filter((funder) => funder.name.trim() !== "");
}

const RE_ABBREVIATION = /^(?:the\s+)?[\p{Lu}\p{N}&.-]{2,10}$/u;

/**
 * "NIH", "NSF", "the ERC" — a name the Crossref Funder Registry and ROR will not match.
 * Those registries list the full name ("National Institutes of Health"), and a
 * submission system can only link the article to its funder when the name matches.
 */
export function looksAbbreviated(name: string): boolean {
  return RE_ABBREVIATION.test(name.trim());
}

function elsevierItem(
  funder: FunderInput,
  templates: FundingTemplates,
  options: FundingOptions
): string {
  const name = stripFinalPeriod(funder.name);
  const location = stripFinalPeriod(funder.location);
  const grants = parseGrantNumbers(funder.grants);
  const where =
    location === "" ? name : t(templates.withLocation, { name, location });
  if (grants.length === 0) {
    return where;
  }
  return `${where} ${t(templates.elsevierBracket, {
    label: options.grantLabel(grants.length),
    numbers: grants.join(", "),
  })}`;
}

/** "This work was supported by A [grant number 1]; B [grant numbers 2, 3]; and C." */
function elsevierStatement(
  funders: readonly FunderInput[],
  templates: FundingTemplates,
  options: FundingOptions
): string {
  const items = funders.map((funder) =>
    elsevierItem(funder, templates, options)
  );
  const joined =
    items.length < 2
      ? (items[0] ?? "")
      : `${items.slice(0, -1).join(templates.elsevierSeparator)}${templates.elsevierFinalSeparator}${items.at(-1)}`;
  return asSentence(t(templates.lead, { funders: joined }));
}

function narrativeAward(
  funder: FunderInput,
  templates: FundingTemplates,
  options: FundingOptions
): string {
  const grants = parseGrantNumbers(funder.grants);
  const recipients = stripFinalPeriod(funder.recipients);
  if (grants.length === 0) {
    return recipients === "" ? "" : t(templates.narrativeTo, { recipients });
  }
  const values = {
    label: options.grantLabel(grants.length),
    numbers: listConjunction(grants, options.locale),
    recipients,
  };
  return recipients === ""
    ? t(templates.narrativeAward, values)
    : t(templates.narrativeAwardTo, values);
}

function narrativeItem(
  funder: FunderInput,
  templates: FundingTemplates,
  options: FundingOptions
): string {
  const details = [
    stripFinalPeriod(funder.url),
    narrativeAward(funder, templates, options),
  ]
    .filter((part) => part !== "")
    .join(templates.detailSeparator);
  const name = stripFinalPeriod(funder.name);
  return details === ""
    ? name
    : `${name}${t(templates.narrativeDetails, { details })}`;
}

/** "This work was supported by A (https://a.org; grant number 1 to J.S.) and B." */
function narrativeStatement(
  funders: readonly FunderInput[],
  templates: FundingTemplates,
  options: FundingOptions
): string {
  const items = funders.map((funder) =>
    narrativeItem(funder, templates, options)
  );
  return asSentence(
    t(templates.lead, { funders: listConjunction(items, options.locale) })
  );
}

/** The funding sentence for the named funders, or "" when there are none. */
export function fundingSentence(
  funders: readonly FunderInput[],
  templates: FundingTemplates,
  options: FundingOptions
): string {
  const list = namedFunders(funders);
  if (list.length === 0) {
    return "";
  }
  return options.style === "elsevier"
    ? elsevierStatement(list, templates, options)
    : narrativeStatement(list, templates, options);
}
